import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ShieldCheck } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const clientTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "Asia/Kolkata";

export default function ManageBooking() {
  const { token } = useParams();
  const [appt, setAppt] = useState(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("view");    // view | reschedule | done
  const [meta, setMeta] = useState(null);
  const [date, setDate] = useState();
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/appointments/manage/${token}`);
      setAppt(data.appointment);
      setName(data.prospect_name);
    } catch { toast.error("Invalid link"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [token]);

  const beginReschedule = async () => {
    setPhase("reschedule");
    try {
      const { data } = await axios.get(`${API}/public/available-slots`);
      setMeta(data);
    } catch { toast.error("Could not load slots"); }
  };

  const doReschedule = async () => {
    if (!slot) return;
    setBusy(true);
    try {
      await axios.post(`${API}/public/appointments/manage/${token}/reschedule`, { slot_utc: slot.start_utc });
      toast.success("Rescheduled");
      setPhase("done");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reschedule failed");
    } finally { setBusy(false); }
  };

  const doCancel = async () => {
    if (!window.confirm("Cancel this Discovery Call?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/public/appointments/manage/${token}/cancel`);
      toast.success("Cancelled");
      setPhase("done");
      load();
    } catch (err) {
      toast.error("Cancel failed");
    } finally { setBusy(false); }
  };

  if (loading) return <Shell><div className="p-10 text-center text-muted-foreground">Loading…</div></Shell>;
  if (!appt) return <Shell><div className="p-10 text-center">This link isn't valid.</div></Shell>;

  const cancelled = appt.status === "Cancelled";

  const slotsByDate = {};
  (meta?.slots || []).forEach((s) => {
    const key = new Date(s.start_utc).toLocaleDateString("en-CA", { timeZone: clientTz });
    (slotsByDate[key] ||= []).push(s);
  });
  const selectedKey = date?.toLocaleDateString("en-CA", { timeZone: clientTz });
  const daySlots = selectedKey ? (slotsByDate[selectedKey] || []) : [];
  const disabled = (d) => !slotsByDate[d.toLocaleDateString("en-CA", { timeZone: clientTz })];

  return (
    <Shell>
      <div className="mx-auto max-w-2xl px-6 py-12">
        <div className="mb-6 flex items-center gap-3 text-sm text-primary">
          <ShieldCheck className="h-4 w-4" /> Manage your Discovery Call
        </div>
        <Card className="border-border bg-white p-6 md:p-8" data-testid="manage-card">
          <div className="mb-6">
            <div className="overline mb-1 text-muted-foreground">Booked for</div>
            <div className="font-dashboard text-2xl font-bold" data-testid="manage-slot">
              {new Date(appt.slot_datetime_utc).toLocaleString("en-IN", {
                weekday: "long", day: "2-digit", month: "long", year: "numeric",
                hour: "2-digit", minute: "2-digit", timeZone: clientTz,
              })}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {name} · <span className="inline-flex items-center gap-1 rounded-full border border-border bg-secondary/60 px-2 py-0.5">{appt.status}</span>
            </div>
          </div>

          {phase === "done" && (
            <div className="rounded-md border border-primary/30 bg-primary/5 p-4 text-center" data-testid="manage-done">
              <CheckCircle2 className="mx-auto mb-2 h-8 w-8 text-primary" />
              <div className="text-sm">{cancelled ? "Your call has been cancelled." : "Your call has been rescheduled."}</div>
            </div>
          )}

          {phase === "view" && !cancelled && (
            <div className="flex flex-wrap gap-3">
              <Button onClick={beginReschedule} data-testid="reschedule-btn">Reschedule</Button>
              <Button variant="outline" onClick={doCancel} disabled={busy} data-testid="cancel-btn">
                <XCircle className="mr-1 h-4 w-4" /> Cancel call
              </Button>
            </div>
          )}

          {phase === "reschedule" && (
            <div className="mt-6 grid gap-8 md:grid-cols-[auto_1fr]">
              <div>
                <div className="overline mb-3 text-muted-foreground">Pick a new date</div>
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => { setDate(d); setSlot(null); }}
                  disabled={(d) => d < new Date(new Date().setHours(0,0,0,0)) || disabled(d)}
                  className="rounded-md border"
                />
              </div>
              <div>
                <div className="overline mb-3 text-muted-foreground">Available slots</div>
                {!date ? <p className="text-sm text-muted-foreground">Pick a date first.</p> :
                  daySlots.length === 0 ? <p className="text-sm text-muted-foreground">No open slots on this date.</p> :
                  <div className="grid grid-cols-3 gap-2">
                    {daySlots.map((s) => {
                      const t = new Date(s.start_utc).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: clientTz });
                      const active = slot?.start_utc === s.start_utc;
                      return (
                        <button key={s.start_utc} onClick={() => setSlot(s)}
                          className={"rounded-md border px-2 py-2 text-xs " + (active ? "border-primary bg-primary text-primary-foreground" : "border-border bg-white hover:bg-secondary")}
                          data-testid={`resched-slot-${t.replace(":", "")}`}>
                          {t}
                        </button>
                      );
                    })}
                  </div>
                }
                <Button className="mt-4 w-full" onClick={doReschedule} disabled={!slot || busy} data-testid="confirm-reschedule">
                  {busy ? "Rescheduling…" : "Confirm new slot"}
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="marketing hero-bg min-h-screen">
      <header className="border-b border-black/5 bg-white/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-6 py-4">
          <BrandLogo size="sm" />
        </div>
      </header>
      {children}
    </div>
  );
}
